/**
 * Calcula la luminancia relativa de un color (WCAG 2.1)
 *
 * @param {string} hexColor - Color en formato hex (#RRGGBB o #RGB)
 * @returns {number} Luminancia relativa (0-1)
 */
const getRelativeLuminance = (hexColor) => {
  let hex = hexColor.replace('#', '');

  // Expand 3-char hex to 6-char hex (#RGB -> #RRGGBB)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const [rs, gs, bs] = [r, g, b].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calcula el ratio de contraste entre dos colores (WCAG 2.1)
 *
 * @param {string} color1 - Primer color (hex)
 * @param {string} color2 - Segundo color (hex)
 * @returns {number} Ratio de contraste (1-21)
 */
export const getContrastRatio = (color1, color2) => {
  const l1 = getRelativeLuminance(color1);
  const l2 = getRelativeLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Convierte un color a rgba con alpha. Acepta hex #RRGGBB o #RGB.
 * Si no es hex válido, devuelve el color original (fallback seguro).
 */
const withAlpha = (color, alpha) => {
  if (typeof color !== 'string') return color;
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (!/^[0-9A-F]{6}$/i.test(hex)) return color;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Valida y ajusta un color de rol para uso en chat
 * Garantiza contraste WCAG AA (4.5:1 mínimo)
 *
 * @param {string} rolColor - Color del rol (hex)
 * @param {Object} theme - MUI theme object
 * @returns {Object} Colores validados para usar en el chat
 */
export const prepareRoleColorForChat = (rolColor, theme) => {
  const isDark = theme.palette.mode === 'dark';
  const backgroundColor = isDark
    ? theme.palette.grey[800]
    : theme.palette.background.paper;

  // Guard: messages without a role color (e.g. stored before agent assignment)
  // get neutral theme-based colors instead of crashing on null.replace()
  if (!rolColor || typeof rolColor !== 'string') {
    const neutralColor = theme.palette.text.secondary;
    return {
      borderColor: 'transparent',
      nameText: neutralColor,
      avatarBg: theme.palette.action.selected,
      avatarBgGradient: `linear-gradient(135deg, ${theme.palette.action.selected}, ${theme.palette.action.hover})`,
      avatarShadow: 'none',
      avatarText: neutralColor,
      contrastRatio: 1,
      meetsAccessibility: false,
    };
  }

  // Calcular contraste del color del rol contra el fondo
  const contrast = getContrastRatio(rolColor, backgroundColor);

  // WCAG AA requiere 4.5:1 para texto normal
  const meetsWCAG = contrast >= 4.5;

  return {
    // Color para el borde lateral (siempre el color base)
    borderColor: rolColor,

    // Color para el nombre del agente (ajustado si es necesario)
    nameText: meetsWCAG
      ? rolColor
      : theme.palette.text.primary,  // Fallback si no cumple contraste

    // Color para el avatar
    avatarBg: rolColor,
    avatarBgGradient: `linear-gradient(135deg, ${rolColor}, ${withAlpha(rolColor, 0.87)})`,
    avatarShadow: `0 2px 8px ${withAlpha(rolColor, isDark ? 0.35 : 0.25)}`,
    avatarText: theme.palette.getContrastText
      ? theme.palette.getContrastText(rolColor)
      : theme.palette.primary.contrastText,

    // Metadata
    contrastRatio: contrast,
    meetsAccessibility: meetsWCAG
  };
};

/**
 * Valida si un color es válido en formato hex
 *
 * @param {string} color - Color a validar
 * @returns {boolean}
 */
export const isValidHexColor = (color) => {
  return /^#[0-9A-F]{6}$/i.test(color);
};
